
import AppSendMessage from "@/components/mail/SendMessageDialog.vue";
import { Sale } from "@/models/base";
import { MessagesService } from "@/services/messages-service";
import { EmailTemplateInfo } from "@/utils/email-template-info";
import Vue from "vue";
import { SaleService } from "../../services/sale-service";
import { base64ToBlob, downloadBlob } from "../../utils/blob";
import { IMail, ISendMessageDialogContact } from "../../utils/mail";
import { CustomerService } from "@/services/customer-service";
import { UblService } from "../../plugins/ubl/services/ubl-service";
import { UserConfigService } from "../../services/user-config-service";
import { AuthService } from "../../services/auth-service";
import { MODULE } from "../../config/modules";
import { DossierConfigService } from "@/services/dossier-config-service";
import { Tier } from "@/utils/payments/tiers";
import { PrintSendSetting } from "@/models/dossier-configs/sale-config";
import { PrintContext, SalePrintSettings } from "@/utils/sale-layouts/print-settings";
import AppSalePrintExtraOptionsModal from "./SalePrintExtraOptionsModal.vue";
import { MailSettings } from "@/models/mail-settings";
import { MailSettingsService } from "@/services/mail-settings-service";
import { HttpError } from "@/utils/services/http-error";

export default Vue.extend({
	data() {
		return {
			templates: [] as EmailTemplateInfo[],
			selectedTemplate: null as null | EmailTemplateInfo,
			content: "",
			subject: "",
			htmlSelected: false,
			ublFile: null as null | File,
			addReadConfirm: false,
			extraBcc: "",
			additionalBcc: "",
			files: [] as File[],
			templateAttachments: [] as File[],
			ready: false,
			extraPrintOptionsModalOpen: false,
			defaultMailSettings: null as null | MailSettings,
			UserConfigService
		};
	},
	props: {
		sale: {
			type: Object as () => Sale,
			required: true
		},
		extraPrintOptions: Boolean
	},
	computed: {
		contacts(): ISendMessageDialogContact[]{
			if(!this.sale.Contact){
				return [];
			}
			return this.sale.Contact.getSendMessageDialogContacts();
		},
		clientMailAddress(): string {
			if (this.sale.Contact != null) {
				let mail = this.sale.Contact.getDefaultEmailForSaleTypeId(this.sale.TypeID);
				if (mail) {
					return mail;
				}
			}
			if (this.sale.InvoiceAddress) {
				let mail = this.sale.InvoiceAddress.getEmailForSaleTypeId(this.sale.TypeID) || this.sale.InvoiceAddress.getDefaultEmail();
				if (mail){
					return mail;
				}
			}
			if (this.sale.Contact) {
				return this.sale.Contact.getDefaultEmail();
			}
			return "";
		},
		templateItems(): any {
			return this.templates.map(t => {
				return {
					text: t.Name,
					value: t
				};
			});
		},
		attachements(): File[] {
			let res = [] as File[];
			if (this.files) {
				res.push(...this.files);
			}
			if (this.ublFile) {
				res.push(this.ublFile);
			}
			res.push(...this.templateAttachments);
			return res;
		},
		salePrintContext():PrintContext{
			return PrintContext.EMAIL_SEND;
		},
		modalFullHeight():boolean {
			return this.UserConfigService.getGeneralSettings().MaximizeEmailModal;
		},
		readConfirmIcon():string{
			if (this.addReadConfirm) {
				return "eye";
			}
			return "eye-slash";
		}
	},
	watch: {
		async selectedTemplate(template: EmailTemplateInfo | null) {
			if (!template) {
				this.content = "";
				this.subject = this.$ct("email-subject").toString();
			} else {
				this.htmlSelected = template.IsHTML;
				let fetchedTemplate = await MessagesService.getSaleTemplate(template.ID);
				let r = await MessagesService.getComputedSaleMail(template, this.sale);
				this.content = r.Body;
				this.subject = r.Subject;
				this.addReadConfirm = fetchedTemplate.SendReadConfirmation;
				this.templateAttachments = r.Attachments;
			}
		},
		"sale.TypeID": {
			immediate: true,
			handler() {
				this.setExtraBcc();
				this.selectDefaultTemplate();
				this.selectDefaultMailSettings();
			}
		}
	},
	methods: {
		close() {
			this.$emit("close");
		},
		trigger(value: EmailTemplateInfo) {
			this.selectedTemplate = value;
		},
		async onSent(e: IMail) {
			if (this.sale.InvoiceAddress != null && (this.sale.InvoiceAddress.Email || "") == "" && e.to != "") {
				let text = this.$ct("do-you-want-to-save-this-email-address-in-the-invoice-address");
				let confirm = await this.$confirmBuilder(text.toString()).setTitle(this.$ct("invoice-address-has-no-email")).show();
				if (confirm){
					let contact = await CustomerService.getContact(this.sale.InvoiceAddress.ContactID);
					for (let address of contact.Addresses){
						if (address.ID == this.sale.InvoiceAddress.ID){
							address.Email = e.to;
						}
					}
					await CustomerService.putContact(contact);
					this.sale.InvoiceAddress.Email = e.to;
				}
			}
			let saleConfig = DossierConfigService.getSaleConfig(this.sale.TypeID);
			if (saleConfig.PrintSendSetting == PrintSendSetting.PrintAfterSend){
				let settings = new SalePrintSettings(PrintContext.EMAIL_SEND);
				settings.forceSingleCoppy = true;
				await SaleService.printSale(this.sale, 0, settings);
			}
			this.$emit("sent");
			this.close();

		},
		async sendHandler(mail: IMail, settings:MailSettings) {
			await SaleService.sendSale(this.sale.ID, mail, settings, this.addReadConfirm);
		},
		async downloadDocument() {
			for (let file of this.files){
				console.log("Downloading");
				downloadBlob(file, file.name);
			}
		},
		async attachUblIfRequired() {
			if (AuthService.hasModule(MODULE.ELECTRONISCHE_FACTURATIE) && this.sale.TypeID < 3) {
				if (UserConfigService.getPluginSettings().UBL.SendUBLInvoice) {
					let rawUbl = await UblService.createInvoiceUbl(this, this.sale);
					let file = new File([rawUbl], `${this.sale.ComputedFriendlyID}-ubl.xml`, {type: "application/xml"});
					this.ublFile = file;
				}
			}
		},
		selectDefaultTemplate() {
			let defaultID = DossierConfigService.getSaleConfig(this.sale.TypeID).DefaultEmailTemplateID;
			if (defaultID != 0) {
				let template = this.templates.find(t => t.ID = defaultID);
				if (template) {
					this.selectedTemplate = template;
				}
			}
		},
		async selectDefaultMailSettings(){
			let defaultID = UserConfigService.getSaleSettings().getSaleConfig(this.sale.TypeID).DefaultMailSettingsID;
			if (!defaultID) {
				this.defaultMailSettings = null;
				return;
			}
			try{
				this.defaultMailSettings = await MailSettingsService.getSettingsById(defaultID);
			}catch(err){
				let error = err as HttpError;
				if (error.status == 404){
					this.defaultMailSettings = null;
					error.dontShow = true;
				}
			}
		},

		canUseReadNotifications() {
			return AuthService.wfCompany.Tier >= Tier.PROFESSIONAL;
		},
		setExtraBcc(){
			let saleConfig = DossierConfigService.getSaleConfig(this.sale.TypeID);
			this.extraBcc = saleConfig.ExtraBCCAddress;
			this.additionalBcc = saleConfig.AdditionalBCCAddress;
			if(this.sale.InvoiceAddress?.Email.includes(";")||this.sale.InvoiceAddress?.Email.includes(",")){
				return;
			}
			let a = this.sale.InvoiceAddress?.Email.trim().split(" ");
			if(a && a.length>1){
				this.extraBcc += (this.extraBcc ? ", ":"") + a.splice(1).join(", ");
			}
		},
		async fetchFiles(){
			let result = await SaleService.downloadAsJson(this.sale, 0, new SalePrintSettings(PrintContext.EMAIL_SEND));
			this.files = result.map(r=>{
				console.log(r.FileName, r.MimeType);
				return new File([base64ToBlob(r.Data, r.MimeType)], r.FileName, {type: r.MimeType});
			});
		},
		closeSalePrintExtraOptionsModal(){
			if (this.ready){
				this.extraPrintOptionsModalOpen = false;
			}else{
				this.close();
			}
		},
		onFiles(files:File[]){
			this.extraPrintOptionsModalOpen = false;
			this.files = files;
			this.ready = true;
		}
	},
	async mounted() {
		await this.attachUblIfRequired();
		this.templates = (await MessagesService.getSaleTemplates());
		if (this.sale.InvoiceAddress == null) {
			let contact = await CustomerService.getContact(this.sale.ContactID);
			if (contact.Addresses) {
				this.sale.InvoiceAddress = contact.Addresses.find(a => a.ID == this.sale.InvoiceAddressID) || null;
			}
		}

		if (this.templates.length > 0) {
			this.selectedTemplate = this.templates[0];
			this.selectDefaultTemplate();
		}

		if (this.extraPrintOptions){
			this.extraPrintOptionsModalOpen = true;
			return;
		}
		await this.fetchFiles();
		this.ready = true;
	},
	components: {
		AppSendMessage,
		AppSalePrintExtraOptionsModal
	},
	i18nEx: {
		componentPrefix: "components.sales"
	},
});
